/**
 * @fileoverview Image Upload Module - Redux Slice
 * State management for image upload modal workflow
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import type { CropData, ImageUploadState, UploadConfig } from "../types"
import { DEFAULT_UPLOAD_CONFIG } from "../utils/imageUploadConfig"

/**
 * Initial state for image upload
 */
const initialState: ImageUploadState = {
  isOpen: false,
  isUploading: false,
  currentStep: "select",
  previewUrl: null,
  cropData: null,
  error: null,
  config: DEFAULT_UPLOAD_CONFIG,
  sessionId: null
}

/**
 * Image upload Redux slice
 * Manages modal state and upload workflow (select → crop → upload)
 */
export const imageUploadSlice = createSlice({
  name: "imageUpload",
  initialState,
  reducers: {
    /**
     * Open upload modal with configuration
     *
     * @param state - Current state
     * @param action - Payload with config and sessionId
     */
    openUpload: (
      state,
      action: PayloadAction<{
        config?: Partial<UploadConfig>
        sessionId: string
      }>
    ) => {
      state.isOpen = true
      state.currentStep = "select"
      state.error = null
      state.previewUrl = null
      state.cropData = null
      state.isUploading = false
      state.sessionId = action.payload.sessionId

      if (action.payload.config) {
        state.config = { ...DEFAULT_UPLOAD_CONFIG, ...action.payload.config }
      }
    },

    /**
     * Close upload modal and reset state
     */
    closeUpload: (state) => {
      state.isOpen = false
      state.currentStep = "select"
      state.error = null
      state.previewUrl = null
      state.cropData = null
      state.isUploading = false
      state.sessionId = null
    },

    /**
     * Set selected file (move to crop step)
     *
     * @param state - Current state
     * @param action - Payload with preview URL
     */
    setSelectedFile: (state, action: PayloadAction<{ previewUrl: string }>) => {
      state.previewUrl = action.payload.previewUrl
      state.currentStep = "crop"
      state.error = null
    },

    /**
     * Set crop data from cropper component
     */
    setCropData: (state, action: PayloadAction<CropData>) => {
      state.cropData = action.payload
    },

    /**
     * Start upload process (move to upload step)
     */
    startUpload: (state) => {
      state.isUploading = true
      state.currentStep = "upload"
      state.error = null
    },

    /**
     * Upload succeeded (close modal)
     */
    setUploadSuccess: (state) => {
      state.isUploading = false
      state.isOpen = false
      state.currentStep = "select"
      state.previewUrl = null
      state.cropData = null
      state.error = null
      state.sessionId = null
    },

    /**
     * Upload failed (show error, stay open)
     */
    setUploadError: (state, action: PayloadAction<string>) => {
      state.isUploading = false
      state.error = action.payload
    },

    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null
    },

    /**
     * Reset upload to initial step (stay open)
     */
    resetUpload: (state) => {
      state.isOpen = false
      state.currentStep = "select"
      state.previewUrl = null
      state.cropData = null
      state.error = null
      state.isUploading = false
    }
  }
})

/**
 * Action creators
 */
export const {
  openUpload,
  closeUpload,
  setSelectedFile,
  setCropData,
  startUpload,
  setUploadSuccess,
  setUploadError,
  clearError,
  resetUpload
} = imageUploadSlice.actions

/**
 * Reducer
 */
export const imageUploadReducer = imageUploadSlice.reducer

export default imageUploadReducer
